import { useTranslation } from "react-i18next";
import { formatPrice, type Currency } from "@nh/shared";
import { Alert } from "@/shared/ui/core";
import { SkeletonText } from "@/shared/ui/ui";
import { DerivedValue, FormSection } from "@/features/admin/components";
import { pricedCurrencies } from "../packagePricing";
import type { PackagePreviewState } from "../usePackagePreview";

export interface PackagePriceSummaryProps {
  state: PackagePreviewState;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * "Esto es lo que va a costar"
 *
 * El contrato no deja escribir el precio de un paquete: se escribe el
 * descuento y el servidor lo deriva. Bien — pero sin este bloque, quien
 * arma el paquete teclea un 20 % a ciegas, guarda, vuelve a la lista y
 * ahí se entera de que el paquete se cobra en 3 480 pesos. Y si no era
 * eso lo que quería, otra vuelta.
 *
 * Aquí la cuenta se hace mientras se edita, con las MISMAS funciones del
 * contrato que usará el servidor (`discountedPrices`, que redondea el
 * centavo hacia abajo, a favor de quien compra). Todo lo que se enseña
 * va marcado como CALCULADO —marco punteado, icono de sumatoria, y la
 * palabra "Calculado" para el lector de pantalla— porque un número que
 * sale de una cuenta y se pinta como un campo invita a intentar
 * cambiarlo.
 *
 * Y lo que NO se puede calcular con certeza NO se inventa:
 *
 *  · Una moneda en la que solo algunos productos tienen precio no se
 *    suma: se avisa. Sumar los que sí tienen daría un paquete más barato
 *    de lo que cuesta, y esa cifra acabaría en la tienda.
 *  · El descuento EXTRA para franquiciatarios no se compone con este.
 *    El contrato guarda el porcentaje pero no dice sobre qué se aplica
 *    —¿sobre la lista o sobre el precio ya descontado?—, y elegir por mi
 *    cuenta sería exactamente la promesa que este bloque existe para
 *    evitar.
 * ═══════════════════════════════════════════════════════════════════
 */
export function PackagePriceSummary({ state }: PackagePriceSummaryProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-US" : "es-MX";
  const { preview, loading, error, truncated, discountPercent } = state;

  const currencies = pricedCurrencies(preview);

  return (
    <FormSection
      title={t("Lo que va a costar")}
      description={t(
        "No se teclea: sale de los productos del paquete y del descuento. El servidor hace esta misma cuenta al guardar.",
      )}
      columns={1}
    >
      {error && (
        <Alert variant="error" title={t("No se pudo cargar el catálogo de productos")}>
          {t("Sin él no se puede calcular el precio. Recarga la página.")}
        </Alert>
      )}

      {loading && !error && (
        <div aria-busy="true">
          <span className="sr-only">{t("Calculando el precio…")}</span>
          <SkeletonText lines={3} />
        </div>
      )}

      {!loading && !error && (
        <PriceGrid
          currencies={currencies}
          state={state}
          locale={locale}
          discountPercent={discountPercent}
        />
      )}

      {truncated && (
        <Alert variant="warning">
          {t(
            "El catálogo tiene más productos de los que caben en el selector: si un paquete usa uno de los que faltan, su precio aquí sale incompleto.",
          )}
        </Alert>
      )}

      {preview.missingProductIds.length > 0 && (
        <Alert variant="warning" title={t("Falta un producto del paquete")}>
          {t(
            "Hay {{count}} producto(s) que el catálogo no encuentra. Hasta que se arreglen, esta cuenta está incompleta.",
            { count: preview.missingProductIds.length },
          )}
        </Alert>
      )}

      {preview.incompleteCurrencies.length > 0 && (
        <Alert variant="warning" title={t("Una moneda se quedó fuera")}>
          {t(
            "En {{currencies}} no todos los productos tienen precio, así que el paquete no se puede cobrar en esa moneda. Ponles precio o quítalos del paquete.",
            { currencies: preview.incompleteCurrencies.join(", ") },
          )}
        </Alert>
      )}
    </FormSection>
  );
}

interface PriceGridProps {
  currencies: Currency[];
  state: PackagePreviewState;
  locale: string;
  discountPercent: number | null;
}

/**
 * Las cifras. Una tarjeta por moneda con lo que se cobra, y debajo lo
 * que costaba suelto y lo que se ahorra: los tres números juntos son el
 * argumento de venta del paquete, y separados no dicen nada.
 */
function PriceGrid({ currencies, state, locale, discountPercent }: PriceGridProps) {
  const { t } = useTranslation();
  const { preview } = state;

  if (preview.lines.length === 0) {
    return (
      <p className="text-[13px] leading-relaxed text-ds-ink-3">
        {t("Elige los productos del paquete y aquí aparece lo que va a cobrar.")}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {currencies.map((currency) => {
        const price = preview.prices[currency] as number;
        const list = preview.listPrices[currency] as number;
        const saved = preview.savings[currency] ?? 0;

        return (
          <DerivedValue
            key={currency}
            emphasis
            label={t("Se cobra en {{currency}}", { currency })}
            value={formatPrice(price, currency, locale)}
            note={
              <>
                {t("Suelto")}:{" "}
                <s className="text-ds-ink-3">{formatPrice(list, currency, locale)}</s>
                {" · "}
                {t("Ahorro")}: {formatPrice(saved, currency, locale)}
              </>
            }
          />
        );
      })}

      {currencies.length === 0 && (
        <p className="text-[13px] leading-relaxed text-ds-ink-3 sm:col-span-2">
          {t(
            "Ninguno de los productos elegidos tiene precio todavía, así que no hay nada que cobrar.",
          )}
        </p>
      )}

      <DerivedValue
        label={t("VP del paquete")}
        value={preview.volumePoints}
        note={
          preview.volumePointsIncomplete
            ? t("Hay productos sin VP capturados: este total se queda corto.")
            : t("La suma de los VP de sus productos, por su cantidad.")
        }
      />

      <DerivedValue
        label={t("Descuento aplicado")}
        value={discountPercent === null ? "—" : `${discountPercent}%`}
        note={
          discountPercent === null
            ? t("Escribe un descuento entero para ver el precio final.")
            : t("{{units}} unidades en {{lines}} productos.", {
                units: preview.units,
                lines: preview.lines.length,
              })
        }
      />
    </div>
  );
}
